import type { TemplateDependencyValidationError } from '@festina/configuration-general-types/configuration'
import { assertNotEmpty, type Optional } from '@festina-life/core'
import { IconButton } from '@festina-life/ui/actions'
import { useTranslate } from '@festina-life/ui/hooks'
import { useMemo } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import type { ValidationFieldValue } from '../../types/templateModel'
import type { ValidationErrorWithLocation } from '../../uiModels/errors/types'
import { InheritanceIndicator } from '../inheritanceIndicator/InheritanceIndicator'
import { indexedFieldErrors } from '../templateFieldsTable/fieldsHelpers'
import { ValidationFieldValueTable } from './ValidationFieldValueTable'

type Props = {
  validations: ValidationFieldValue[]
  onValidationChange?: (
    nextValue: Optional<ValidationFieldValue>,
    index: number,
  ) => Promise<void>
  fieldPath: string
  errors?: Optional<
    ValidationErrorWithLocation<TemplateDependencyValidationError>[]
  >
  isShowingTitle?: boolean
}

/**
 * Displays the ValidationFieldValue contents for a ValueSetupField
 */
export const FieldValidationsValueDetails = ({
  validations,
  fieldPath,
  onValidationChange,
  errors,
  isShowingTitle = true,
}: Props) => {
  const { templateVersion } = useTemplateContext()
  const translate = useTranslate()
  const validationErrors = useMemo(
    () =>
      indexedFieldErrors({
        fieldPath,
        errors: errors ?? [],
        indexedFieldName: 'validations',
      }),
    [errors, fieldPath],
  )

  assertNotEmpty(templateVersion?.versionInfo)

  return (
    <div className="flex flex-col gap-y-8">
      {validations.map((validation, index) => (
        // oxlint-disable-next-line react/no-array-index-key -- we don't have a unique key
        <div className="flex flex-col gap-y-2" key={`validation/${index}`}>
          <div className="mb-2 flex flex-row items-center justify-between gap-x-2 text-sm text-gray-300">
            <div className="flex items-center gap-x-2">
              {isShowingTitle && (
                <h4
                  className={`m-0 uppercase ${
                    validationErrors[index] ? 'text-error-500' : ''
                  }`}
                >
                  {translate(
                    'templateViewer.validationsDetails.validationIndex',
                    {
                      index,
                    },
                  )}
                </h4>
              )}
              <InheritanceIndicator value={validation} />
            </div>

            {!!onValidationChange && validation.origin === validation.path && (
              <IconButton
                onClick={() => onValidationChange(null, index)}
                iconName="MinusCircle"
              />
            )}
          </div>
          <ValidationFieldValueTable
            errors={validationErrors[index]}
            value={validation}
            onChange={
              onValidationChange
                ? (nextValue) => onValidationChange(nextValue, index)
                : undefined
            }
          />
        </div>
      ))}
    </div>
  )
}
